import React, {Suspense, lazy, useEffect, useState} from 'react'
import {Redirect, Route, Switch} from 'react-router-dom'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {loadMenus} from '../pages/definition/menu/Api'
import {IMenuModel} from '../pages/definition/menu/MenuModel'
import {PAGE_COMPONENT_REGISTRY} from './pageComponentRegistry'
import { XmlTariffDetailPage } from '../pages/definition/xml-tariff-detail/XmlTariffDetailPage'
import { ReservationPage } from '../pages/reservation/ReservationPage'
import { RentPage } from '../pages/rent/RentPage'
import { HrProcessFlowPage } from '../pages/hr/process-flow/HrProcessFlowPage'
import { HR_PROCESS_FLOW_PATH } from '../pages/hr/process-flow/HrProcessStepModel'
import { HrProcessPage } from '../pages/hr/process/HrProcessPage'
import { HR_LOCATION_PATH, LocationPage } from '../pages/hr/location/LocationPage'
import { HR_QUESTION_PATH, QuestionPage } from '../pages/hr/question/QuestionPage'
import { MailTemplatePage } from '../pages/hr/mail-template/MailTemplatePage'
import { HR_MAIL_TEMPLATE_PATH } from '../pages/hr/mail-template/MailTemplateModel'
import { JobPage } from '../pages/hr/job/JobPage'
import { HR_JOB_PATH } from '../pages/hr/job/JobModel'
import { CompanyProfilePage } from '../pages/hr/company/CompanyProfilePage'
import { HR_COMPANY_PATH } from '../pages/hr/company/CompanyProfileModel'
import { CorporatePage } from '../pages/admin/corporate/CorporatePage'
import { ADMIN_CORPORATE_PATH } from '../pages/admin/corporate/CorporateModel'
import { useIsAdmin } from '../../shared/utils/useIsAdmin'
import { ApplicationPage } from '../pages/hr/application/ApplicationPage'
import { HR_APPLICATION_PATH } from '../pages/hr/application/ApplicationModel'
import { TrackingPage } from '../pages/hr/tracking/TrackingPage'
import { HR_TRACKING_PATH } from '../pages/hr/tracking/TrackingModel'
import { CandidatePage } from '../pages/hr/candidate/CandidatePage'
import { CandidateDetailPage } from '../pages/hr/candidate/CandidateDetailPage'
import { HR_CANDIDATE_PATH } from '../pages/hr/candidate/CandidateModel'
import { HR_UNLINKED_CV_PATH, UnlinkedCvPage } from '../pages/hr/unlinked-cv/UnlinkedCvPage'
import { TagPage } from '../pages/hr/tag/TagPage'
import { JobDashboardPage } from '../pages/hr/job/JobDashboardPage'
import { CallPoolPage } from '../pages/hr/call-pool/CallPoolPage'
import { HR_CALL_POOL_PATH } from '../pages/hr/call-pool/CallPoolModel'
import { HR_TAG_PATH } from '../pages/hr/tag/TagModel'
import { CalendarPage } from '../pages/hr/calendar/CalendarPage'
import { GoogleCallbackPage } from '../pages/hr/calendar/GoogleCallbackPage'
import { GOOGLE_CALLBACK_PATH, HR_CALENDAR_PATH } from '../pages/hr/calendar/CalendarModel'
import { ContactPage } from '../pages/hr/contact/ContactPage'
import { HR_CONTACT_PATH } from '../pages/hr/contact/Api'
import { InterviewPage } from '../pages/hr/interview/InterviewPage'
import { HR_INTERVIEW_PATH } from '../pages/hr/interview/InterviewModel'
import { UserCorporatePage } from '../pages/admin/user-corporate/UserCorporatePage'
import { ADMIN_USER_CORPORATE_PATH } from '../pages/admin/user-corporate/UserCorporateModel'

interface DynamicRoute {
  path: string
  component: React.ComponentType<any>
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

function resolveComponentFromMenu(menu: IMenuModel): React.ComponentType<any> | undefined {
  const name = menu.componentName?.trim()
  if (!name) return undefined
  return PAGE_COMPONENT_REGISTRY[name]
}

/** Tam ekran splash-screen kullanma; z-index ile tüm tıklamaları keser */
function LightRouteFallback() {
  return (
    <div className='d-flex justify-content-center align-items-center py-15'>
      <span className='spinner-border text-primary' role='status' />
    </div>
  )
}

export function PrivateRoutes() {
  const [routes, setRoutes] = useState<DynamicRoute[]>([])
  const [menusLoading, setMenusLoading] = useState(true)
  const isAdmin = useIsAdmin()

  const BuilderPageWrapper = lazy(() => import('../pages/layout-builder/BuilderPageWrapper'))
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))

  useEffect(() => {
    function flattenMenus(items: IMenuModel[]): IMenuModel[] {
      const result: IMenuModel[] = []
      items.forEach((item) => {
        result.push(item)
        if (item.children && item.children.length > 0) {
          result.push(...flattenMenus(item.children))
        }
      })
      return result
    }

    loadMenus()
      .then((res) => {
        const allItems = flattenMenus(res.data.payload)
        const seen = new Set<string>()
        const result: DynamicRoute[] = []

        allItems
          .filter((m) => m.isActive)
          .forEach((m) => {
            const path = normalizePath(m.path.split('?')[0])
            const component = resolveComponentFromMenu(m)
            if (!seen.has(path) && component) {
              seen.add(path)
              result.push({path, component})
            }
          })

        setRoutes(result)
      })
      .catch(() => {
        setRoutes([])
      })
      .finally(() => {
        setMenusLoading(false)
      })
  }, [])

  const MenuTestPage = PAGE_COMPONENT_REGISTRY.MenuTestPage

  return (
    <Suspense fallback={<LightRouteFallback />}>
      <Switch>
        <Route path='/dashboard' component={DashboardWrapper} />
        <Route path='/builder' component={BuilderPageWrapper} />
        {/* Menüden gelen /hr/candidate route'u alt adresleri de yakaladığı için detay önce tanımlanır */}
        <Route path={`${HR_CANDIDATE_PATH}/detail/:id`} component={CandidateDetailPage} />
        <Route path={`${HR_JOB_PATH}/dashboard/:id`} component={JobDashboardPage} />
        <Route path={GOOGLE_CALLBACK_PATH} component={GoogleCallbackPage} />
        {routes.map((r) => (
          <Route key={r.path} path={r.path} component={r.component} />
        ))}
        <Route path='/crafted/pages/profile' component={ProfilePage} />
        <Route path='/crafted/pages/wizards' component={WizardsPage} />
        <Route path='/crafted/widgets' component={WidgetsPage} />
        <Route path='/crafted/account' component={AccountPage} />
        <Route path='/apps/chat' component={ChatPage} />
        <Route path='/definition/xml-tariff-detail' component={XmlTariffDetailPage} />
        <Route path='/reservation' component={ReservationPage} />
        <Route path='/rent' component={RentPage} />
        <Route path={HR_PROCESS_FLOW_PATH} component={HrProcessFlowPage} />
        <Route path='/hr/process' component={HrProcessPage} />
        <Route path={HR_LOCATION_PATH} component={LocationPage} />
        <Route path={HR_QUESTION_PATH} component={QuestionPage} />
        <Route path={HR_MAIL_TEMPLATE_PATH} component={MailTemplatePage} />
        <Route path={HR_JOB_PATH} component={JobPage} />
        <Route path={HR_COMPANY_PATH} component={CompanyProfilePage} />
        <Route path={HR_APPLICATION_PATH} component={ApplicationPage} />
        <Route path={HR_TRACKING_PATH} component={TrackingPage} />
        <Route path={HR_CANDIDATE_PATH} component={CandidatePage} />
        <Route path={HR_UNLINKED_CV_PATH} component={UnlinkedCvPage} />
        <Route path={HR_TAG_PATH} component={TagPage} />
        <Route path={HR_CALL_POOL_PATH} component={CallPoolPage} />
        <Route path={HR_CALENDAR_PATH} component={CalendarPage} />
        <Route path={HR_CONTACT_PATH} component={ContactPage} />
        <Route path={HR_INTERVIEW_PATH} component={InterviewPage} />
        {isAdmin && <Route path={ADMIN_CORPORATE_PATH} component={CorporatePage} />}
        {isAdmin && <Route path={ADMIN_USER_CORPORATE_PATH} component={UserCorporatePage} />}
        {MenuTestPage && <Route path='/menu-test' component={MenuTestPage} />}
        <Redirect from='/auth' to='/dashboard' />
        <Redirect exact from='/' to='/dashboard' />
        {!menusLoading && <Redirect to='error/404' />}
      </Switch>
    </Suspense>
  )
}
